import { useEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { TitlePill, TITLE_PILL_MAX_WIDTH } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { ProfileMenu, type ProfileMenuProps } from "@acto/taskpane/field-sales-2/components/patterns/ProfileMenu";
import { Search, SearchCloseButton } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { FilterPopover, type FilterPopoverGroup } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";

const SCROLL_COLLAPSE_THRESHOLD_PX = 24;

const NavigationBar = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  // Above the in-content menus that scroll up under it (CalendarSelectMenu zIndex 6), matching
  // ShellTopBar — the transparent gradient background otherwise lets them show through.
  zIndex: 7,
  width: "100%",
  maxWidth: "100%",
  padding: `${theme.fieldSalesTwo.spacing[72]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[10]}`,
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 34.6%, rgba(240,238,237,0) 100%)`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  overflow: "hidden",
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
  gap: theme.fieldSalesTwo.spacing[8],
}));

// `margin-right: auto` is kept in both states so the SearchSlot's right
// edge stays anchored at the trailing-slot boundary throughout. Snapping
// the margin to 0 on expand caused a one-frame layout reflow where items
// packed to the start before flex-grow took over, producing a visible
// rightward bounce of the search pill's right edge. With auto retained,
// flex-grow on SearchSlot collapses the auto margin to 0 (flex-grow has
// priority over auto margins), so the layout reaches its final right-
// anchored shape immediately and the pill grows purely leftward as
// max-width transitions.
const TitleSlot = styled("div")({
  flex: "0 1 auto",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  maxWidth: TITLE_PILL_MAX_WIDTH,
  // Explicit numeric collapsed-from height so the max-height transition animates both ways (CSS
  // can't interpolate to/from `none`); comfortably fits the 1–2 line title pill.
  maxHeight: "5rem",
  marginRight: "auto",
  transition:
    "max-width 280ms cubic-bezier(0.22, 1, 0.36, 1), max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease",
  "& > *": {
    transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
  },
  // Collapse width AND height when hidden: with a filter active the pill grows a second (subtitle)
  // line, and a width-only collapse left that extra height inflating the row — the large gap under
  // the search bar. Clamping max-height to 0 keeps the row a single line while search is open.
  '&[data-search-expanded="true"]': {
    maxWidth: 0,
    maxHeight: 0,
    opacity: 0,
    pointerEvents: "none",
  },
  '&[data-search-expanded="true"] > *': {
    transform: "translateX(-100%)",
  },
});

// SearchSlot's right edge stays anchored at the trailing-slot boundary; the
// pill grows leftward when expanded. Scroll-collapsed state slides the slot
// rightward into the (now-empty) trailing-slot position.
const SearchSlot = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: "0 1 auto",
  minWidth: 0,
  transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), flex-grow 280ms cubic-bezier(0.22, 1, 0.36, 1)",
  '&[data-shifted="true"]': {
    transform: `translateX(calc(${theme.fieldSalesTwo.spacing[50]} + ${theme.fieldSalesTwo.spacing[8]}))`,
  },
  '&[data-search-expanded="true"]': {
    flex: "1 1 auto",
    transform: "none",
  },
  // With no filter, the collapsed search is a single icon — render it as a circle (matching the other
  // search buttons) instead of the wide capsule the underlying Pill defaults to.
  '&[data-has-filter="false"]:not([data-search-expanded="true"]) > *': {
    width: theme.fieldSalesTwo.spacing[50],
    height: theme.fieldSalesTwo.spacing[50],
    padding: 0,
    justifyContent: "center",
  },
}));

// Fixed 50px slot at the right end of the row. Holds the avatar at rest and
// cross-fades to the close button when search expands. When scroll-collapsed
// (and search closed) the slot slides rightward and fades out — translation
// alone falls 8px short of the clip boundary (slot is 50px wide, the shift
// is 58px, but NavigationBar's 16px right padding leaves the slot's left
// edge at `right − 8`), which previously showed an 8px sliver of the avatar
// inside the overflow:hidden clip. Fading to opacity 0 hides that remainder.
const TrailingSlot = styled("div")(({ theme }) => ({
  position: "relative",
  width: theme.fieldSalesTwo.spacing[50],
  height: theme.fieldSalesTwo.spacing[50],
  flex: "0 0 auto",
  transition: "transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease",
  '&[data-shifted="true"]': {
    transform: `translateX(calc(${theme.fieldSalesTwo.spacing[50]} + ${theme.fieldSalesTwo.spacing[8]}))`,
    opacity: 0,
    pointerEvents: "none",
  },
}));

// Scale on the hidden state: a pure-opacity cross-fade hits opacity 0.5 on
// both items at the midpoint, which composites to ~empty and reads as "first
// disappears, then second appears." Adding a scale gives the eye motion to
// track through the midpoint so the swap reads as a single cross-fade.
const TrailingItem = styled("div")({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transformOrigin: "center",
  transition: "opacity 200ms ease, transform 200ms ease",
  '&[data-hidden="true"]': {
    opacity: 0,
    transform: "scale(0.85)",
    pointerEvents: "none",
  },
});

interface TopBarFilterConfig {
  groups: FilterPopoverGroup[];
  selectedKeys: string[];
  onToggle: (key: string) => void;
  /**
   * When set, the filter button shows a blue dot and the title pill
   * displays the filter summary as a second line. The consumer decides
   * what counts as "active" — e.g., picking the explicit clear-all option
   * should leave this undefined.
   */
  activeSummary?: { firstLabel: string; extraCount: number };
}

interface TopBarSearchConfig {
  value: string;
  onChange: (value: string) => void;
}

interface TopBarProps {
  title: string;
  /** Drives the trailing avatar, which opens the profile menu. Omit to render no avatar. */
  profile?: ProfileMenuProps;
  searchPlaceholder?: string;
  filter?: TopBarFilterConfig;
  search: TopBarSearchConfig;
}

function useScrollCollapsed(threshold: number): boolean {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    function onScroll() {
      setCollapsed(window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return collapsed;
}

export function TopBar({ title, profile, searchPlaceholder, filter, search }: TopBarProps) {
  const t = useTranslations("fieldSalesTwo.search");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("placeholder");
  const scrollCollapsed = useScrollCollapsed(SCROLL_COLLAPSE_THRESHOLD_PX);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  // Collapsing the search (close button or Escape) also clears the term, so reopening starts fresh
  // and the underlying list isn't left filtered by a query the user can no longer see.
  const handleExpandedChange = (expanded: boolean) => {
    setSearchExpanded(expanded);
    if (!expanded) search.onChange("");
  };

  // The scroll-collapse shift only exists to slide the avatar out of the way on scroll; with no
  // profile there is no avatar, so the search + filter stay put. Search expansion also overrides it
  // (the trailing slot then hosts the close button), so it only applies while search is closed.
  const slotsShifted = Boolean(profile) && !searchExpanded && scrollCollapsed;
  const activeSummary = filter?.activeSummary;
  const subtitle = activeSummary
    ? activeSummary.extraCount > 0
      ? `${activeSummary.firstLabel} +${activeSummary.extraCount}`
      : activeSummary.firstLabel
    : undefined;

  return (
    <NavigationBar>
      <Row>
        <TitleSlot data-search-expanded={searchExpanded}>
          <TitlePill subtitle={subtitle}>{title}</TitlePill>
        </TitleSlot>
        <SearchSlot data-shifted={slotsShifted} data-search-expanded={searchExpanded} data-has-filter={Boolean(filter)}>
          <Search
            placeholder={resolvedSearchPlaceholder}
            value={search.value}
            onChange={search.onChange}
            expanded={searchExpanded}
            onExpandedChange={handleExpandedChange}
            onFilterClick={filter ? () => setFilterOpen((v) => !v) : undefined}
            filterButtonRef={filterButtonRef}
            hasActiveFilters={activeSummary != null}
          />
        </SearchSlot>
        {/* With no avatar, the slot is only needed to host the search-close button while search is
            open; omitting it at rest lets the search + filter use the full row width. */}
        {(profile || searchExpanded) && (
          <TrailingSlot data-shifted={slotsShifted}>
            {profile && (
              <TrailingItem data-hidden={searchExpanded}>
                <ProfileMenu {...profile} />
              </TrailingItem>
            )}
            <TrailingItem data-hidden={!searchExpanded}>
              <SearchCloseButton onClick={() => handleExpandedChange(false)} />
            </TrailingItem>
          </TrailingSlot>
        )}
      </Row>
      {filter && (
        <FilterPopover
          anchorRef={filterButtonRef}
          isOpen={filterOpen}
          onClose={() => setFilterOpen(false)}
          groups={filter.groups}
          selectedKeys={filter.selectedKeys}
          onToggle={filter.onToggle}
        />
      )}
    </NavigationBar>
  );
}
